import React, { useState, useEffect } from 'react';
import { Terminal, ShieldAlert, Cpu, Database, RefreshCcw, Search, Sparkles, FileText } from 'lucide-react';

const FaultAudit = ({ theme = 'dark' }) => {
  const [logs, setLogs] = useState([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [remediation, setRemediation] = useState(null);
  const [isRemediating, setIsRemediating] = useState(false);
  const isDark = theme === 'dark';

  useEffect(() => {
    setLogs([
      { id: '1', timestamp: new Date().toISOString(), level: 'INFO', message: 'Kernel process initiated.', traceId: 'TR-7721-AX', source: 'Core.Kernel', stackTrace: 'Kernel initialized at App.js @ Line 42' },
      { id: '2', timestamp: new Date().toISOString(), level: 'INFO', message: 'Auth cluster connected successfully.', traceId: 'TR-7721-AX', source: 'Identity.SSO', stackTrace: 'Identity handshake successful at AuthFlow.js @ Line 128' },
      { id: '3', timestamp: new Date().toISOString(), level: 'WARN', message: 'Latency spike detected in region us-east-1.', traceId: 'TR-8110-LB', source: 'Cloud.Network', stackTrace: 'Network timeout warning in MicroserviceMesh.js @ Line 55' },
    ]);
  }, []);

  const triggerFault = () => {
    setIsSimulating(true);
    setTimeout(() => {
      setLogs((prev) => [{ id: Math.random().toString(), timestamp: new Date().toISOString(), level: 'FATAL', message: 'Database connection timeout in mesh-v4-prod.', traceId: `TR-${Math.floor(Math.random() * 9000 + 1000)}-ERR`, source: 'Services.DB', stackTrace: 'Uncaught Error: Connection timed out after 5000ms\n  at Database.connect (db-client.js:88:12)' }, ...prev]);
      setIsSimulating(false);
    }, 1500);
  };

  const generateRemediation = () => {
    setIsRemediating(true); setRemediation(null);
    setTimeout(() => {
      setRemediation(`[NEXUS PROPOSAL]\n1. Recycle Database connection pool in Mesh Cluster V4.\n2. Increase timeout buffer from 5s to 15s in kernel config.\n3. Validate node health via silent telemetry handshake.`);
      setIsRemediating(false);
    }, 2000);
  };

  return (
    <div data-testid="fault-audit-screen" className={`h-full flex gap-3 p-3 overflow-hidden ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#f5f6f8]'}`}>
      <div className="flex-1 flex flex-col gap-4 overflow-hidden">
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Active Exceptions', value: logs.filter((l) => l.level === 'FATAL').length.toString(), icon: <ShieldAlert className="text-red-500" /> },
            { label: 'System Uptime', value: '99.98%', icon: <RefreshCcw className="text-green-500" /> },
            { label: 'CPU Load', value: '24%', icon: <Cpu className="text-blue-500" /> },
            { label: 'DB Latency', value: '18ms', icon: <Database className="text-purple-500" /> },
          ].map((stat, i) => (
            <div key={i} className={`p-4 rounded-lg flex items-center justify-between border ${isDark ? 'bg-[#151515] border-[#333]' : 'bg-white border-slate-200'}`}>
              <div>
                <p className={`text-[9px] uppercase font-bold tracking-widest ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{stat.label}</p>
                <p className={`text-xl font-bold mt-1 ${isDark ? 'text-gray-200' : 'text-slate-900'}`}>{stat.value}</p>
              </div>
              {stat.icon}
            </div>
          ))}
        </div>

        <div className={`flex-1 border rounded-lg flex flex-col overflow-hidden shadow-2xl ${isDark ? 'bg-black border-[#333]' : 'bg-white border-slate-200'}`}>
          <div className={`p-3 border-b flex items-center justify-between ${isDark ? 'bg-[#111] border-[#333]' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center gap-2 text-xs font-mono"><Terminal size={14} className="text-blue-500" /><span className={isDark ? 'text-gray-400' : 'text-slate-600'}>REAL-TIME KERNEL EVENTS</span></div>
            <button data-testid="trigger-fault-btn" onClick={triggerFault} disabled={isSimulating}
              className="bg-red-600/10 hover:bg-red-600/20 text-red-500 border border-red-500/30 px-3 py-1 rounded text-[10px] font-bold uppercase transition-all disabled:opacity-50">
              {isSimulating ? 'SIMULATING...' : 'Trigger DB Timeout'}
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 font-mono text-[10px] leading-relaxed">
            {logs.map((log) => (
              <div key={log.id} onClick={() => { setSelectedLog(log); setRemediation(null); }}
                className={`mb-1 flex gap-4 p-2 rounded cursor-pointer transition-colors border ${selectedLog?.id === log.id ? (isDark ? 'border-blue-500/50 bg-[#151515]' : 'border-blue-500/50 bg-blue-50') : 'border-transparent'} ${isDark ? 'hover:bg-[#1a1a1a]' : 'hover:bg-slate-50'}`}>
                <span className={isDark ? 'text-gray-600' : 'text-slate-400'}>[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                <span className={`shrink-0 font-bold w-12 ${log.level === 'FATAL' ? 'text-red-500' : log.level === 'ERROR' ? 'text-red-400' : log.level === 'WARN' ? 'text-amber-500' : 'text-blue-400'}`}>{log.level}</span>
                <span className={`shrink-0 border-r pr-2 ${isDark ? 'text-gray-500 border-[#222]' : 'text-slate-400 border-slate-200'}`}>{log.traceId}</span>
                <span className={`flex-1 truncate ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`w-96 border rounded-lg flex flex-col overflow-hidden shrink-0 shadow-2xl ${isDark ? 'bg-[#151515] border-[#333]' : 'bg-white border-slate-200'}`}>
        <div className={`p-4 border-b flex items-center gap-2 ${isDark ? 'border-[#333] bg-[#1a1a1a]' : 'border-slate-200 bg-slate-50'}`}>
          <Search size={16} className="text-blue-500" />
          <h3 className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-white' : 'text-slate-900'}`}>Stack Inspector</h3>
        </div>
        <div className="flex-1 p-4 space-y-6 overflow-y-auto">
          {selectedLog ? (
            <>
              <div className="space-y-4">
                <div className={`p-2 rounded text-[9px] text-center font-bold uppercase tracking-wide ${isDark ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                  Log entry selected - inspect details below
                </div>
                <div className={`p-3 rounded border ${isDark ? 'bg-black border-[#333]' : 'bg-slate-50 border-slate-200'}`}>
                  <p className="text-[10px] text-gray-500 uppercase font-black mb-2">Parsed Trace-ID</p>
                  <p className="text-xs font-mono text-blue-400">{selectedLog.traceId}</p>
                </div>
                <div className={`p-3 rounded border ${isDark ? 'bg-black border-[#333]' : 'bg-slate-50 border-slate-200'}`}>
                  <p className="text-[10px] text-gray-500 uppercase font-black mb-2">Stack Analysis</p>
                  <p className={`text-[10px] font-mono whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{selectedLog.stackTrace || 'No stack trace available.'}</p>
                </div>
              </div>
              <div className={`pt-4 border-t ${isDark ? 'border-[#333]' : 'border-slate-200'}`}>
                <button data-testid="ai-remediation-btn" onClick={generateRemediation} disabled={isRemediating}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-600/20">
                  <Sparkles size={14} />{isRemediating ? 'Analyzing...' : 'Request AI Remediation'}
                </button>
                {remediation && (
                  <div className="mt-4 p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-green-500 mb-2 font-black uppercase text-[10px]"><Sparkles size={12} />Autonomous Remediation</div>
                    <p className={`text-[10px] font-mono whitespace-pre-wrap leading-loose ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{remediation}</p>
                    <button className="w-full mt-3 py-1.5 bg-green-600/10 hover:bg-green-600/20 text-green-500 border border-green-500/30 rounded text-[10px] font-bold uppercase transition-all">Confirm remediation path</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-30 py-20">
              <FileText size={48} /><p className="text-xs font-mono uppercase tracking-widest">Select a log entry to inspect</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaultAudit;
